/**
 * Scoped signal & strategy catalog management component.
 *
 * Embeds in StreamDetails, Profile, and UserDetails pages to provide
 * per-scope signal and strategy customization using the same UX as
 * the global admin page.
 *
 * Two tabs:
 *  - Signals: scoped signal catalog overrides (replaces global when any exist)
 *  - Strategies: custom strategies created for this scope
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { signalsApi, strategiesApi } from '@/api/conversationSignals';
import type { ConversationSignal, ConversationStrategy } from '@/types/conversationSignals';
import { SignalModal, StrategyDialog } from '@/components/conversation-signals';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

interface Props {
  scopeType: 'stream' | 'user';
  scopeUuid: string;
  fallbackUserUuid?: string;
  fallbackUserLabel?: string;
}

const ScopedSignalCatalog: React.FC<Props> = ({
  scopeType,
  scopeUuid,
  fallbackUserUuid,
  fallbackUserLabel,
}) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  // ── Signals state ───────────────────────────────────────────────────────────
  const [signalModalOpen, setSignalModalOpen] = useState(false);
  const [editSignal, setEditSignal] = useState<ConversationSignal | null>(null);
  const [deleteSignalTarget, setDeleteSignalTarget] = useState<ConversationSignal | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  // ── Strategies state ────────────────────────────────────────────────────────
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [editStrategy, setEditStrategy] = useState<ConversationStrategy | null>(null);
  const [deleteStrategyTarget, setDeleteStrategyTarget] = useState<ConversationStrategy | null>(null);

  const {
    data: scopedSignalsPages,
    isLoading: isLoadingSignals,
    fetchNextPage: fetchNextSignalsPage,
    hasNextPage: hasNextSignalsPage,
    isFetchingNextPage: isFetchingNextSignalsPage,
  } = useInfiniteQuery({
    queryKey: ['conversation-signals', 'scoped', scopeType, scopeUuid],
    queryFn: ({ pageParam }) =>
      signalsApi.list({
        scope_type: scopeType,
        scope_uuid: scopeUuid,
        page_token: pageParam as string | undefined,
        max_results: 20,
      }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!scopeUuid,
  });

  const {
    data: scopedStrategiesPages,
    isLoading: isLoadingScopedStrategies,
    fetchNextPage: fetchNextScopedStrategiesPage,
    hasNextPage: hasNextScopedStrategiesPage,
    isFetchingNextPage: isFetchingNextScopedStrategiesPage,
  } = useInfiniteQuery({
    queryKey: ['conversation-strategies', 'scoped', scopeType, scopeUuid],
    queryFn: ({ pageParam }) =>
      strategiesApi.list({
        scope_type: scopeType,
        scope_uuid: scopeUuid,
        page_token: pageParam as string | undefined,
        max_results: 20,
      }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!scopeUuid,
  });

  // For SignalModal strategy picker: include scoped + global strategies.
  const {
    data: modalStrategiesPages,
    isLoading: isLoadingModalStrategies,
    fetchNextPage: fetchNextModalStrategiesPage,
    hasNextPage: hasNextModalStrategiesPage,
    isFetchingNextPage: isFetchingNextModalStrategiesPage,
  } = useInfiniteQuery({
    queryKey: ['conversation-strategies', 'scope-with-global', scopeType, scopeUuid],
    queryFn: ({ pageParam }) =>
      strategiesApi.list({
        scope_type: scopeType,
        scope_uuid: scopeUuid,
        include_global: true,
        page_token: pageParam as string | undefined,
        max_results: 100,
      }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!scopeUuid,
  });

  // For stream scope only: detect whether operator (user scope) has custom signals.
  const {
    data: fallbackUserSignalsData,
    isLoading: isLoadingFallbackUserSignals,
  } = useInfiniteQuery({
    queryKey: ['conversation-signals', 'fallback-user', fallbackUserUuid],
    queryFn: ({ pageParam }) =>
      signalsApi.list({
        scope_type: 'user',
        scope_uuid: fallbackUserUuid as string,
        page_token: pageParam as string | undefined,
        max_results: 1,
      }),
    getNextPageParam: () => undefined,
    initialPageParam: undefined as string | undefined,
    enabled: scopeType === 'stream' && !!fallbackUserUuid && !!scopeUuid,
  });

  // Load all option pages for strategy selector.
  useEffect(() => {
    if (hasNextModalStrategiesPage && !isFetchingNextModalStrategiesPage && !isLoadingModalStrategies) {
      fetchNextModalStrategiesPage();
    }
  }, [
    hasNextModalStrategiesPage,
    isFetchingNextModalStrategiesPage,
    isLoadingModalStrategies,
    fetchNextModalStrategiesPage,
  ]);

  const signals: ConversationSignal[] = useMemo(
    () => scopedSignalsPages?.pages.flatMap((p) => p.results) ?? [],
    [scopedSignalsPages]
  );
  const signalsTotal = scopedSignalsPages?.pages?.[0]?.total_results ?? signals.length;

  // Custom scoped strategies only (exclude global rows in the table).
  const scopedStrategies: ConversationStrategy[] = useMemo(
    () =>
      (scopedStrategiesPages?.pages.flatMap((p) => p.results) ?? []).filter(
        (s) => s.creator_uuid !== null || s.stream_uuid !== null
      ),
    [scopedStrategiesPages]
  );
  const scopedStrategiesTotal = scopedStrategiesPages?.pages?.[0]?.total_results ?? scopedStrategies.length;

  const allStrategies: ConversationStrategy[] = useMemo(() => {
    const rows = modalStrategiesPages?.pages.flatMap((p) => p.results) ?? [];
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(row.key)) return false;
      seen.add(row.key);
      return true;
    });
  }, [modalStrategiesPages]);

  const invalidateSignals = () => {
    queryClient.invalidateQueries({
      queryKey: ['conversation-signals', 'scoped', scopeType, scopeUuid],
    });
  };

  const invalidateStrategies = () => {
    queryClient.invalidateQueries({
      queryKey: ['conversation-strategies', 'scoped', scopeType, scopeUuid],
    });
    queryClient.invalidateQueries({
      queryKey: ['conversation-strategies', 'scope-with-global', scopeType, scopeUuid],
    });
  };

  // ── Signal mutations ────────────────────────────────────────────────────────
  const toggleActiveMutation = useMutation({
    mutationFn: ({ signal, value }: { signal: ConversationSignal; value: boolean }) =>
      signalsApi.update(signal.key, { is_active: value }),
    onMutate: ({ signal }) => setTogglingKey(signal.key),
    onSettled: () => {
      setTogglingKey(null);
      invalidateSignals();
    },
  });

  const deleteSignalMutation = useMutation({
    mutationFn: () => signalsApi.delete(deleteSignalTarget!.key),
    onSuccess: () => {
      invalidateSignals();
      setDeleteSignalTarget(null);
    },
  });

  // ── Strategy mutations ──────────────────────────────────────────────────────
  const deleteStrategyMutation = useMutation({
    mutationFn: () => strategiesApi.delete(deleteStrategyTarget!.key),
    onSuccess: () => {
      invalidateStrategies();
      setDeleteStrategyTarget(null);
    },
  });

  const fallbackUserSignalsCount =
    fallbackUserSignalsData?.pages?.flatMap((p) => p.results)?.length ?? 0;
  const fallbackHasCustomSignals = fallbackUserSignalsCount > 0;

  const isLoading =
    isLoadingSignals ||
    isLoadingScopedStrategies ||
    isLoadingModalStrategies ||
    isLoadingFallbackUserSignals;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Signals
              {signalsTotal > 0 && (
                <Chip label={signalsTotal} size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Strategies
              {scopedStrategiesTotal > 0 && (
                <Chip label={scopedStrategiesTotal} size="small" color="info" sx={{ height: 18, fontSize: 10 }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* ── Signals tab ── */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Signal Catalog Overrides
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditSignal(null);
              setSignalModalOpen(true);
            }}
          >
            Add Signal
          </Button>
        </Box>

        {signals.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {scopeType === 'stream' && fallbackHasCustomSignals
              ? `No custom signals are defined for this stream. Draft generation will use custom signals defined by its operator${
                  fallbackUserLabel ? ` (${fallbackUserLabel})` : ''
                }. Add stream-level signals here to override both operator and global catalogs.`
              : `No custom signals defined for this ${scopeType}. The global catalog is used. Add signals here to override.`}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This {scopeType} uses a custom signal catalog ({signals.length}{' '}
            {signals.length === 1 ? 'signal' : 'signals'}). The global catalog is not used.
          </Alert>
        )}

        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                <TableCell>Label</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Strategies</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((s) => (
                <TableRow key={s.key} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: s.filter_colors?.['dot'] ?? '#ccc',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {s.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {s.ui_category ? (
                      <Chip
                        label={s.ui_category}
                        size="small"
                        sx={{
                          fontSize: 11,
                          bgcolor: s.filter_colors?.['bg'] ?? undefined,
                          color: s.filter_colors?.['text'] ?? undefined,
                          border: s.filter_colors?.['border']
                            ? `1px solid ${s.filter_colors['border']}`
                            : undefined,
                        }}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {(s.strategies ?? []).map((st, idx) => (
                        <Typography key={st.key} variant="caption" color="text.secondary">
                          {idx + 1}. {st.label}
                        </Typography>
                      ))}
                      {(!s.strategies || s.strategies.length === 0) && (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={s.is_active}
                      onChange={(e) =>
                        toggleActiveMutation.mutate({ signal: s, value: e.target.checked })
                      }
                      disabled={togglingKey === s.key}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditSignal(s);
                          setSignalModalOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteSignalTarget(s)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {signals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No custom signals
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasNextSignalsPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => fetchNextSignalsPage()}
              disabled={isFetchingNextSignalsPage}
              startIcon={
                isFetchingNextSignalsPage ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {isFetchingNextSignalsPage ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* ── Strategies tab ── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Custom Strategies
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditStrategy(null);
              setStrategyDialogOpen(true);
            }}
          >
            Add Strategy
          </Button>
        </Box>

        {scopedStrategies.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No custom strategies defined for this {scopeType}. Only global strategies are available
            for signal assignment. Add strategies here to create scope-specific ones.
          </Alert>
        )}

        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                <TableCell>Label</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scopedStrategies.map((s) => (
                <TableRow key={s.key} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {s.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                      {s.description ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditStrategy(s);
                          setStrategyDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteStrategyTarget(s)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {scopedStrategies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No custom strategies
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasNextScopedStrategiesPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => fetchNextScopedStrategiesPage()}
              disabled={isFetchingNextScopedStrategiesPage}
              startIcon={
                isFetchingNextScopedStrategiesPage ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isFetchingNextScopedStrategiesPage ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* ── Modals ── */}
      <SignalModal
        open={signalModalOpen}
        signal={editSignal}
        allStrategies={allStrategies}
        scopeType={scopeType}
        scopeUuid={scopeUuid}
        onClose={() => setSignalModalOpen(false)}
        onSaved={() => {
          setSignalModalOpen(false);
          invalidateSignals();
          invalidateStrategies();
        }}
      />

      <StrategyDialog
        open={strategyDialogOpen}
        strategy={editStrategy}
        scopeType={scopeType}
        scopeUuid={scopeUuid}
        onClose={() => setStrategyDialogOpen(false)}
        onSaved={() => {
          setStrategyDialogOpen(false);
          invalidateStrategies();
        }}
      />

      {/* Signal delete confirm */}
      <Dialog
        open={!!deleteSignalTarget}
        onClose={() => setDeleteSignalTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Signal Override</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteSignalTarget?.label}</strong> from this {scopeType}'s catalog?
          </Typography>
          {deleteSignalMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to delete.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteSignalTarget(null)}
            disabled={deleteSignalMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteSignalMutation.mutate()}
            disabled={deleteSignalMutation.isPending}
            startIcon={
              deleteSignalMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteSignalMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Strategy delete confirm */}
      <Dialog
        open={!!deleteStrategyTarget}
        onClose={() => setDeleteStrategyTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Strategy</DialogTitle>
        <DialogContent>
          <Typography>
            Delete strategy <strong>{deleteStrategyTarget?.label}</strong>? This removes it from
            any signal assignments.
          </Typography>
          {deleteStrategyMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to delete.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteStrategyTarget(null)}
            disabled={deleteStrategyMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteStrategyMutation.mutate()}
            disabled={deleteStrategyMutation.isPending}
            startIcon={
              deleteStrategyMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteStrategyMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScopedSignalCatalog;
